<template>
  <v-progress-linear v-if="isLoading" indeterminate color="primary"></v-progress-linear>
<info-button v-if="!overview" class="top-20 pa-0 ma-0 right-0 position-fixed" header="Urlaubsregeln">
  <div v-html="vacationRulesText"></div>
</info-button>
  <v-container v-if="!overview">
    <div class="d-flex flex-column align-center mt-4 text-center">
      <h2>{{`Urlaubswünsche ${year}`}}</h2>
      <p>{{`Abgabe bis ${vacationPeriod?.deadline.toLocaleDateString('de-DE')}`}}</p>
    </div>

    <v-container class="mt-4 mb-4">
      <v-row class="d-flex flex-row align-content-center justify-space-around">
        <text-with-dot class="flex-1-0-0 text-center" label-text="Osterferien" color="yellow"></text-with-dot>
        <text-with-dot class="flex-1-0-0 text-center" label-text="Sommerferien" color="green"></text-with-dot>
      </v-row>
      <v-row class="d-flex flex-row align-content-center">
        <text-with-dot class="flex-1-0-0 text-center" label-text="Herbstferien" color="orange"></text-with-dot>
        <text-with-dot class="flex-1-0-0 text-center" label-text="Winterferien" color="blue"></text-with-dot>
      </v-row>
    </v-container>

    <div class="d-flex flex-column justify-space-between pa-4 mb-4">
      <v-row class="flex-row">
        <small class="flex-1-0-0 text-left">Urlaubsanspruch: <b>{{sumUrlaub}}</b></small>
      </v-row>
      <v-row class="flex-row">
        <small class="flex-1-0-0 text-left">Notwendige Urlaubsplanung: <b>{{maxUrlaub}}</b></small>
      </v-row>
      <v-row class="flex-row">
        <small class="flex-1-0-0 text-left">Geplante Tage: <b>{{wishedDays}}</b></small>
      </v-row>
      <v-row class="flex-row">
        <small class="flex-1-0-0 text-left">Entlastungstage anspruch: <b>{{entlastungsTage}}</b></small>
      </v-row>
    </div>
    
    <v-alert v-if="vacationDataError" type="error" class="mb-4">Die Urlaubsdaten konnten nicht geladen werden. Bitte die Seite neu laden.</v-alert>

    <template v-if="!vacationDataError">
    <v-btn-toggle v-model="selectedPrio" class="prio-toggle mb-4" mandatory style="height:auto">
      <v-btn class="prio-btn" :variant="getVariant('1')" :base-color="getBaseColor('1')"  value='1'>
        <span>Prio 1</span><br />
        <span class="text-subtitle-2">Hoch</span>
      </v-btn>
      <v-btn class="prio-btn" :variant="getVariant('2')" :base-color="getBaseColor('2')"  value='2'>
        <span>Prio 2</span><br />
        <span class="text-subtitle-2">Mittelhoch</span>
      </v-btn>
      <v-btn class="prio-btn" :variant="getVariant('3')" :base-color="getBaseColor('3')"  value='3'>
        <span>Prio 3</span><br />
        <span class="text-subtitle-2">Mittel</span>
      </v-btn>
      <v-btn class="prio-btn" :variant="getVariant('4')" :base-color="getBaseColor('4')"  value='4'>
        <span>Prio 4</span><br />
        <span class="text-subtitle-2">Niedrig</span>
      </v-btn>
      <v-btn class="prio-btn" :variant="getVariant('ET')" :base-color="getBaseColor('ET')"  value='ET'>
        <span>ET</span><br />
        <span class="text-subtitle-2">Entlastungstage</span>
      </v-btn>
    </v-btn-toggle>

    <div class="d-flex flex-column align-center" v-if="selectedPrio === 'ET'">
      <ReliefDaysInput v-model="reliefDays" :maxReliefDays="entlastungsTage" :startDate="startDate" :endDate="endDate" />
    </div>
    <div v-else>
      <h3 class="mb-4">Start- und Enddatum auswählen</h3>

      <div class="d-flex flex-column align-center">
        <v-btn-toggle  variant="outlined" v-model="selectedDateInput" class="mb-1 bg-grey-lighten-2">
          <v-btn width="*" variant="plain" color="primary" value="start">start</v-btn>
          <v-btn width="*" variant="plain" color="primary" value="ende">ende</v-btn>
        </v-btn-toggle>
        <v-btn width="*" variant="plain" color="primary" @click="deletePhase">delete</v-btn>
      </div>



      <v-calendar ref="calendar"
                  v-if="startDate && endDate"
                  :attributes="attributes"
                  :expanded="expanded"
                  borderless
                  :min-date="startDate"
                  :max-date="endDate"
                  :disabled-dates="disabledDates"
                  @dayclick="dayClick"
                  :columns="columns"
                  :show-overflow="true"/>
    </div>
    </template>
  </v-container>

  <v-container class="mt-4 d-flex flex-column align-center text-center" v-else>
    <h3>{{`Zusammenfassung Urlaubswunsch ${year}`}}</h3>
    
    <div class="mt-2 text-left">
      <div v-html="vacationSummaryTopText"></div>
      
      <v-list>
        <v-list-item
            v-for="vacationPhase in vacationPhases.filter(vp => vp.startDate !== null && vp.endDate !== null)"
            :key="vacationPhase.index">
          <div>
            <h4>{{ `${vacationPhase.index+1} Prio` }}</h4>
            <h5>{{ `Urlaubstage (geschätzt): ${ Math.round(calculateVacationDays(vacationPhase.startDate, vacationPhase.endDate, maxUrlaub))}` }}</h5>
            <h5>{{ `${toShortDateString(vacationPhase.startDate)} - ${toShortDateString(vacationPhase.endDate)}` }}</h5>
            <h5>{{ `Bemerkung: ${vacationPhase.bemerkung}` }}</h5>
          </div>
        </v-list-item>
      </v-list>
      <div v-html="vacationSummaryBottomText"></div>
    </div>
    <h3>{{`Zusammenfassung Entlastungstage ${year}`}}</h3>
    <div class="mt-2 text-left">
      <v-list>
        <v-list-item v-for="(reliefDay, index) in reliefDays" :key="index">
          <div>
            <h5>{{ `${reliefDay}` }}</h5>
          </div>
        </v-list-item>
      </v-list>
    </div>
    <div v-if="vacationShow">
      <h3 class="mt-4">{{`Bisher genehmigte Urlaube für ${year}`}}</h3>
      <div v-for="(evb, index) in vacationBlocks" :key="index" class="mb-2">
        <div>{{`Urlaub vom ${evb.start.toLocaleDateString('de-DE')} bis ${evb.end.toLocaleDateString('de-DE') } `}}</div>
      </div>

    </div>
  </v-container>

<div class="mt-2 mx-0 pa-0">
  <error-list class="px-4 mb-2 position-relative" :errors="localRulebreaks"></error-list>
</div>

<v-container v-if="!overview" class="mt-auto" style="position: sticky; bottom: 0;" fluid>
  <v-textarea v-if="selectedPrio !== 'ET'" class="mb-4" :label="bemerkungLabel" v-model="localBemerkung">
  </v-textarea>
  
  <div class="mb-1 d-flex justify-space-between flex-end">
    <v-btn  class="ma-0 flex-1-0-0 mr-3" color="secondary" @click="goBack">Verwerfen</v-btn>
<!--      <v-btn  class="ma-0 flex-1-0-0 ml-3" color="primary" @click="onSave(false)">Speichern</v-btn>-->
    <v-btn  v-if="localRulebreaks.length === 0" class="ma-0 flex-1-0-0 ml-3" color="primary" @click="goToOverview">Speichern</v-btn>
  </div>

<!--    <div class="mb-1 d-flex pa-0 justify-space-between flex-end">
    <v-btn  class="ma-0 flex-1-0-0" color="primary" @click="goToOverview">Übersicht</v-btn>
  </div>-->
</v-container>
<v-container v-if="overview && !vacationPeriod.locked" class="mt-auto" style="position: sticky; bottom: 0;" fluid>
  <div class="mb-1 d-flex justify-space-between flex-end">
    <v-btn  class="ma-0 flex-1-0-0 mr-3" color="primary" @click="() => {overview = !overview}" >Zurück</v-btn>
    <v-btn  class="ma-0 flex-1-0-0 ml-3" color="primary" @click="onComplete">Weiter</v-btn>
  </div>
</v-container>
<v-container v-if="overview && vacationPeriod.locked" class="mt-auto" style="position: sticky; bottom: 0;" fluid>
  <div class="mb-1 d-flex justify-space-between flex-end">
    <v-btn  class="ma-0 flex-1-0-0 mr-3" color="primary" @click="goBack" >Zurück</v-btn>
  </div>
</v-container>
</template>

<script lang="ts">

import {GetVacationData, InsertVacationData} from "@/data/vacationData";
import {onBeforeRouteLeave, useRoute} from "vue-router";
import {computed, onMounted, ref, reactive, watch} from "vue";
import {GetVacationPeriod} from "@/data/vacationPeriod";
import {GetShiftSetting} from "@/data/shiftSettings";
import {GetRuleConfig} from "@/data/ruleConfig";
import TextWithDot from "@/components/TextWithDot.vue";
import {useScreens} from "vue-screen-utils";
import {useStore} from "@/store";
import {createDialog, notifyInfo, notifySuccess, notifyWarning} from "vuetify3-dialog";
import router from "@/router";
import {checkRuleBreaksForVacation, getRuleBreak, renderText, RuleBreakData} from "@/rules/ruleEngine";
import infoButton from "@/components/InfoButton.vue";
import errorList from "@/components/ErrorList.vue";
import ReliefDaysInput from "@/components/ReliefDaysInput.vue";
import {addDoc, collection} from "firebase/firestore";
import {db} from "@/main";
import { GetEmployVacationDataDate } from '@/data/employData';
import { convertUtcToGermanLocal } from "@/hooks/utils"
/*
interface VacationPhase {
// Declare properties with types
index: number;
startDate: Date | null;
endDate: Date | null;
bemerkung: string
}*/

export default {
components: {infoButton, TextWithDot, errorList, ReliefDaysInput},
setup(){
  const route = useRoute();
  const store = useStore();
  const employ = ref(store.state.userData.employData);
  
  const summerVacation = ref(store.state.calendarData.newCalendarData.summerVacationData);
  const autumnVacation = ref(store.state.calendarData.newCalendarData.autumnVacationData);
  const winterVacation = ref(store.state.calendarData.newCalendarData.winterVacationData);
  const easterVacation = ref(store.state.calendarData.newCalendarData.easterVacationData);
  
  const calendar = ref(null);
  const startDate = ref<Date | null>(null);
  const endDate = ref<Date | null>(null);
  const userId = store.getters.userId;
  const year = route.params.year;
  const selectedPrio = ref("1");
  const vacationPeriod = ref<VacationPeriod | null>(null);
  const vacationData = ref<VacationData | null>(null);
  const selectedDateInput = ref<string>("start");
  const overview = ref<boolean>(false);
  const vacationShow = ref<boolean>(false);
  const edited = ref<boolean>(false);
  const initialLoad = ref<boolean>(true);
  const localBemerkung = ref<string>('')
  const localRulebreaks = ref<RuleBreakData[]>([]);
  const vacationDataError = ref<boolean>(false);
  const maxUrlaub = ref(store.state.userData.employData.vacations?.vacation1);
  const maxSonderUrlaub = ref(store.state.userData.employData.vacations?.vacation3);
  const localEmployVacationData = ref<EmployVacationData[]>([]);
  const vacationBlocks = computed(() => {
    if (!localEmployVacationData.value || localEmployVacationData.value.length === 0) {
      vacationShow.value = false
      return []
    }
    vacationShow.value = true
    return getVacationBlocks(localEmployVacationData.value)
  })
  const sumUrlaub = computed(() => {
    return maxUrlaub.value + maxSonderUrlaub.value;
  });
  const ruleCfg = ref<RuleConfig | null>(null);
  const vacationRulesText = computed(() =>
    ruleCfg.value
      ? renderText(ruleCfg.value.infoTexts.vacationRules, {maxDays: ruleCfg.value.thresholds.vacationMaxDays, urlaubsanspruch: sumUrlaub.value})
      : ""
  );
  const vacationSummaryTopText = computed(() =>
    ruleCfg.value
      ? renderText(ruleCfg.value.infoTexts.vacationSummaryTop, {deadline: toShortDateString(vacationPeriod.value?.deadline ?? null)})
      : ""
  );
  const vacationSummaryBottomText = computed(() =>
    ruleCfg.value ? renderText(ruleCfg.value.infoTexts.vacationSummaryBottom, {}) : ""
  );
  const shiftSettings = ref<ShiftSettings | null>(null)
  //const shiftSettings = ref<ShiftSettings>(GetShiftSetting(store.state.userData?.employData?.cycleid))
  //const shiftSettings = reactive(GetShiftSetting(store.state.userData?.employData?.cycleid)) as ShiftSettings
  const reliefDays = ref<string[]>([])
  const entlastungsTage = ref<number>(0)
  /*const entlastungsTage = computed(() => {
    const fallbackMax = Number(store.state.userData.employData?.shiftSettings?.reliefDays[0]?.max ?? 0)

    if (!shiftSettings || !Array.isArray(shiftSettings.reliefDays)) {
      console.log("KEIN ShiftSettings")
      return fallbackMax
    }

    console.log("ShiftSettings", shiftSettings)
    return Number(shiftSettings?.reliefDays[0]?.max ?? fallbackMax)
  })
  const entlastungsTage = computed(() => {
    // wenn ShiftSettings noch "pending" oder undefined ist:
    if (!shiftSettings.value || !shiftSettings.value.reliefDays) {
      console.log("KEIN ShiftSettings")
      return Number(store.state.userData.employData?.shiftSettings?.reliefDays[0]?.max ?? 0)
    }
    // wenn beide verfügbar:
    console.log("ShiftSettings", shiftSettings.value)
    return Number(
      store.state.userData.employData?.shiftSettings?.reliefDays[0]?.max ??
      shiftSettings.value.reliefDays[0]?.max ??
      0
    )
  })*/
  
  const normalizeDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }
  
  const wishedDays = ref<number>(0);
  
  const vacationPhases = ref<VacationPhase[]>([
  {
    index: 0,
    startDate: null,
    endDate: null,
    bemerkung: ""
  },
  {
    index: 1,
    startDate: null,
    endDate: null,
    bemerkung: ""
  },
  {
    index: 2,
    startDate: null,
    endDate: null,
    bemerkung: ""
  },
  {
    index: 3,
    startDate: null,
    endDate: null,
    bemerkung: ""
  }]);

  const isLoading = ref<boolean>(false);

  watch(
    [() => store.state.userData, () => shiftSettings.value],
    ([userData, shiftSettingsVal]) => {
      console.log("shiftSettings", shiftSettingsVal)
      console.log("EMPLOYDATA", userData?.employData)

      if (shiftSettingsVal && Array.isArray(shiftSettingsVal.reliefDays)) {
        console.log("ShiftSettings")
        entlastungsTage.value = Number(
          userData?.employData?.shiftSettings?.reliefDays?.[0]?.max ??
          shiftSettingsVal.reliefDays?.[0]?.max ??
          0
        )
      } else {
        console.log("KEIN ShiftSettings")
        entlastungsTage.value = Number(
          userData?.employData?.shiftSettings?.reliefDays?.[0]?.max ?? 0
        )
      }
    },
    { immediate: true }
  )

  watch(() => vacationPhases, () => {
    localRulebreaks.value.splice(0, localRulebreaks.value.length);
    
    wishedDays.value = 0;
    
    // Sammle alle gültigen Phasen
    const validPhases = vacationPhases.value.filter(phase => phase.startDate && phase.endDate);
    
    // Prüfe jede Phase einzeln
    validPhases.forEach((phase) => {
      localRulebreaks.value.push(...checkRuleBreaksForVacation(phase, summerVacation.value, maxUrlaub.value));
      wishedDays.value =  wishedDays.value + calculateVacationDays(phase.startDate, phase.endDate, maxUrlaub.value);
    });
    
    // Prüfe auf aneinandergrenzende Phasen
    if (validPhases.length > 1) {
      const sortedPhases = validPhases
        .slice()
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
      
      for (let i = 0; i < sortedPhases.length - 1; i++) {
        const currentPhase = sortedPhases[i];
        const nextPhase = sortedPhases[i + 1];
        
        // Prüfe ob die Phasen direkt aufeinander folgen (maximal 1 Tag Abstand)
        const dayAfterCurrent = new Date(currentPhase.endDate);
        dayAfterCurrent.setDate(dayAfterCurrent.getDate() + 1);
        
        const daysDifference = Math.floor((nextPhase.startDate.getTime() - currentPhase.endDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysDifference <= 1) {
          localRulebreaks.value.push(getRuleBreak("vacationPhasesAdjacent", {phase1: currentPhase.index + 1, phase2: nextPhase.index + 1}));
        }
      }
    }

    if( Math.round(wishedDays.value) < maxUrlaub.value)
    {
      localRulebreaks.value.push(getRuleBreak("tooFewVacationDays"));
    }
    
    if( Math.round(wishedDays.value) > sumUrlaub.value)
    {
      localRulebreaks.value.push(getRuleBreak("tooManyVacationDays"));
    }
    
    if(initialLoad.value)
    {
      initialLoad.value = false;
      return;
    }
    
    edited.value = true;
  }, {deep: true});

  watch(
      selectedPrio,
      (newValue, oldValue) => {
        if (newValue === oldValue) return;

        selectedDateInput.value = "start";
        
        const oldSelectedPhase = Number(oldValue);
        const newSelectedPhase = Number(newValue);

        if(vacationPhases.value[newSelectedPhase - 1])
        {
          if(vacationPhases.value[newSelectedPhase - 1].startDate)
            calendar.value.focusDate(vacationPhases.value[newSelectedPhase - 1].startDate);
        }
        
        // Make sure the indices are valid before accessing vacationPhases
        if (
            vacationPhases.value[oldSelectedPhase - 1] &&
            vacationPhases.value[newSelectedPhase - 1]
        ) {
          vacationPhases.value[oldSelectedPhase - 1].bemerkung = localBemerkung.value;
          localBemerkung.value = vacationPhases.value[newSelectedPhase - 1].bemerkung;
        }
      }
  );

  const bemerkungLabel = computed (() => {
    return "Bemerkung Prio " + selectedPrio.value;
  });
  const goBack = () => {
    if(edited.value)
    {
      router.push("/urlaubswuensche");
/*      createDialog({ title: "Änderungen verwerfen?",
        text: `Möchtest du deine Änderungen wirklich verwerfen?`,
        buttons: [{ title: "Ja", key: true }, { title: "Nein", key: false }]})
          .then((answer) => {
            if(answer) {
              router.push("/urlaubswuensche");
            }
          })*/
    }
    else{
      router.push("/urlaubswuensche");
    }
  }
  
  const attributes = computed(() => {
    const colors = ['blue', 'green', 'orange', 'purple'];
    // v-calendar wirft "dayIndex"-Fehler bei ungueltigen Daten -> nur echte, gueltige Dates zulassen
    const isValidDate = (d: unknown): d is Date => d instanceof Date && !isNaN(d.getTime());
    // Store enthaelt Ferien mehrerer Jahre (App.vue laedt currentYear + upcomingYear) -> auf min/max begrenzen
    const min = startDate.value;
    const max = endDate.value;
    const inRange = (d: Date): boolean =>
      (!(min instanceof Date) || d.getTime() >= min.getTime()) &&
      (!(max instanceof Date) || d.getTime() <= max.getTime());
    const validInRange = (d: unknown): d is Date => isValidDate(d) && inRange(d);
    let vacationAttributes = vacationPhases.value
        .map((phase, index) => {
          if (isValidDate(phase.startDate) && isValidDate(phase.endDate)) {
            return {
              key: (index + 1).toString(),
              highlight: {
                color: colors[index % colors.length],
                fillMode: 'light',
                contentClass: 'italic',
              },
              popover: {  
                label: `Priorität ${index + 1}`,
              },
              dates: [[phase.startDate, phase.endDate]],
            };
          }
          return null;
        })
        .filter(attribute => attribute !== null);

    const summerVacationAttributes = {
      key: "Sommerferien", // Using name as the key
      bar: 'green',
      popover: {
        label: "Sommerferien",
        visibility: 'hover',
      },
      dates: summerVacation.value
          .filter(vacation => validInRange(vacation.Date))
          .map(vacation => vacation.Date),
    };

    const winterVacationAttributes = {
      key: "Winterferien", // Using name as the key
      bar: 'blue',
      popover: {
        label: "Winterferien",
        visibility: 'hover',
      },
      dates: winterVacation.value
          .filter(vacation => validInRange(vacation.Date))
          .map(vacation => vacation.Date),
    };

    const autumnVacationAttributes = {
      key: "Herbstferien", // Using name as the key
      bar: 'orange',
      popover: {
        label: "Herbstferien",
        visibility: 'hover',
      },
      dates: autumnVacation.value
          .filter(vacation => validInRange(vacation.Date))
          .map(vacation => vacation.Date),
    };

    const easterVacationAttributes = {
      key: "Osterferien", // Using name as the key
      bar: 'yellow',
      popover: {
        label: "Osterferien",
        visibility: 'hover',
      },
      dates: easterVacation.value
          .filter(vacation => validInRange(vacation.Date))
          .map(vacation => vacation.Date),
    };

    const ruleAttributes = [...new Set(localRulebreaks.value.filter(dateObject => validInRange(dateObject.Date)).map(dateObject => dateObject.ruleShortMessage))].map(name => ({
      key: name, // Using name as the key
      bar: 'red',
      popover: {
        label: name,
        visibility: 'hover',
      },
      dates: localRulebreaks.value
          .filter(dateObject => dateObject.ruleShortMessage === name && validInRange(dateObject.Date))
          .map(dateObject => dateObject.Date),
    }));
    
    const allAttributes = [...vacationAttributes, ...[summerVacationAttributes, ...[winterVacationAttributes, ...[autumnVacationAttributes,...[easterVacationAttributes, ...ruleAttributes]]]]];
    // Attribute ohne Datumsangaben entfernen -> v-calendar wirft sonst "dayIndex"-Fehler bei leeren dates
    return allAttributes.filter((attr: any) => Array.isArray(attr.dates) && attr.dates.length > 0);
  })
  const dayClick = (day: CalendarDay) => {
    if(selectedPrio.value === "") return
    if(selectedPrio.value === "ET") return
    if(overview.value) return
    if(startDate.value === null || endDate.value === null) return
    
    if(day.date < startDate.value || day.date > endDate.value)
    {
      notifyInfo("Bitte wähle ein Datum innerhalb des Urlaubszeitraums").then(() => {});
      return;
    }
    
    // Prüfe ob das Datum in einer anderen Phase liegt
    const selectedPhase = Number(selectedPrio.value) - 1
    const isDateBlocked = vacationPhases.value.some((phase, index) => {
      if (index === selectedPhase) return false
      if (!phase.startDate || !phase.endDate) return false
      return day.date >= phase.startDate && day.date <= phase.endDate
    });
    
    if (isDateBlocked) {
      notifyInfo("Dieses Datum ist bereits in einer anderen Priorität belegt").then(() => {})
      return
    }
    
    switch(selectedDateInput.value){
      case "start":
        vacationPhases.value[selectedPhase].startDate = day.date
        selectedDateInput.value = "ende"
        break
      case "ende":
        vacationPhases.value[selectedPhase].endDate = day.date
        selectedDateInput.value = ""
        break
    }
  }

  const deletePhase = () => {
    const selectedPhase = Number(selectedPrio.value) - 1
    vacationPhases.value[selectedPhase].startDate = null
    vacationPhases.value[selectedPhase].endDate = null
  }

  const goToOverview = async () => {
    if (selectedPrio.value !== "ET") {
      const prio = Number(selectedPrio.value)
      vacationPhases.value[prio - 1].bemerkung = localBemerkung.value
    }
    overview.value = !overview.value
    if(localRulebreaks.value.length > 0)
    {
      await createDialog({
        title: "Regelverletzungen",
        text: `Es wurden Regelverletzungen festgestellt. Bitte diese korrigieren.`,
        buttons: [{title: "Ok", key: true}]
      });

      return;
    }

    const savedSuccessfully = await onSave(true)
    if(savedSuccessfully && employ.value)
    {
      await addDoc(collection(db, "mail"), {
        to: employ.value.email,
        message: {
          subject: "Deine Urlaubswünsche und Entlastungstage wurden eingereicht!",
          html:      "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              `  <span>Deine Urlaubswünsche und Entlastungstage für das Jahr <span style=\"color: #BE0081;\">${year}</span> wurden eingereicht!</span>\n` +
              "</div>\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              `  <span>Bis zur Deadline <span style=\"color: #BE0081;\">${vacationPeriod.value.deadline.toLocaleDateString("de-DE")}</span> kannst du die Wünsche noch anpassen!</span>\n` +
              "</div>\n" +
              "\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              "  <span>Es wird nun geprüft, in wie weit deine Wünsche <span style=\"color: #BE0081;\">berücksichtigt</span> werden können.</span>\n" +
              "</div>\n" +
              "\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              "  <span>Beachte, dass der Urlaub und Entlastungstage <span style=\"color: #BE0081;\">noch nicht bestätigt</span> sind!</span>\n" +
              "</div>\n" +
              "\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              "  <span>In der Zwischenzeit kannst du deine eingegebenen Wunschzeiten in der App oder deinen E-Mails <span style=\"color: #BE0081;\">einsehen.</span></span>\n" +
              "</div>\n" +
              "\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              "  <span>Sobald dein Urlaub und deine Entlastungstage bearbeitet wurden, bekommst du eine <span style=\"color: #BE0081;\">Benachrichtigung.</span></span>\n" +
              "</div>"
        },
      });
    }
  }
  
  function toShortDateString(date: Date | null): string {
    if(date === null) return "";
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit'
    });
  }

  function calculateVacationDays(startDate: Date, endDate: Date, maxDays: number): number {
    if (!startDate || !endDate) return 0;

    // Nur Jahr, Monat und Tag verwenden (ohne Uhrzeit, immer UTC!)
    const start = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()));
    const end = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()));

    // Anzahl Kalendertage (inklusive), immer in UTC rechnen!
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const totalDays = Math.floor((end.getTime() - start.getTime()) / millisecondsPerDay) + 1;

    // Ganze Wochen und Resttage
    const fullWeeks = Math.floor(totalDays / 7);
    let remainingDays = totalDays % 7;

    // Ermittlung der Wochentage
    let vacationDays = fullWeeks * (5 * maxDays / 30);

    // Jetzt die verbleibenden Tage prüfen: Wochenende rausrechnen
    for (let i = 0; i < remainingDays; i++) {
        // Wochentag in UTC berechnen
        const dayOfWeek = (start.getUTCDay() + i) % 7; // 0 = Sonntag, 1 = Montag, ..., 6 = Samstag
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            vacationDays++;
        }
    }

    return Math.floor(vacationDays * 100) / 100;
  }

  function getVacationBlocks(vacationData: EmployVacationData[]): Array<{start: Date, end: Date, days: EmployVacationData[]}> {
    if (!vacationData || !vacationData.length) return [];
    
    // Sortiere nach Datum
    const sortedData = vacationData
      .slice() // Kopie erstellen
      .sort((a, b) => new Date(a.date_str).getTime() - new Date(b.date_str).getTime());
    
    const blocks: Array<{start: Date, end: Date, days: EmployVacationData[]}> = [];
    let currentBlock: EmployVacationData[] = [sortedData[0]];
    
    for (let i = 1; i < sortedData.length; i++) {
      const prevDate = new Date(sortedData[i - 1].date_str);
      const currentDate = new Date(sortedData[i].date_str);
      
      // Prüfe ob der aktuelle Tag direkt nach dem vorherigen kommt
      const dayDiff = Math.floor((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (dayDiff === 1) {
        // Aufeinanderfolgender Tag - zur aktuellen Gruppe hinzufügen
        currentBlock.push(sortedData[i]);
      } else {
        // Lücke gefunden - aktuellen Block abschließen und neuen beginnen
        blocks.push({
          start: new Date(currentBlock[0].date_str),
          end: new Date(currentBlock[currentBlock.length - 1].date_str),
          days: currentBlock
        });
        currentBlock = [sortedData[i]];
      }
    }
    
    // Letzten Block hinzufügen
    blocks.push({
      start: new Date(currentBlock[0].date_str),
      end: new Date(currentBlock[currentBlock.length - 1].date_str),
      days: currentBlock
    });
    
    return blocks;
  }

//***** alt Urlaubstageberechnung 20250605 AK */
/*  function calculateVacationDays(startDate: Date, endDate: Date, maxDays: number): number {
    if (!startDate || !endDate) return 0
    
//    console.log("Urlaubstage 1", startDate, endDate)

    // Nur Jahr, Monat und Tag verwenden (ohne Uhrzeit)
    const start = normalizeDate(new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()))
    const end = normalizeDate(new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()))

    // Anzahl Kalendertage (inclusive)
    const millisecondsPerDay = 1000 * 60 * 60 * 24
    const totalDays = Math.floor((end.getTime() - start.getTime()) / millisecondsPerDay) + 1
//    console.log("Urlaubstage 2", totalDays)

    // Ganze Wochen und Resttage
    const fullWeeks = Math.floor(totalDays / 7)
    let remainingDays = totalDays % 7
//    console.log("Urlaubstage 3", fullWeeks, remainingDays)

    // Ermittlung der Wochentage
    let vacationDays = fullWeeks * (5 * maxDays / 30)

    // Jetzt die verbleibenden Tage prüfen: Wochenende rausrechnen
    for (let i = 0; i < remainingDays; i++) {
      const dayOfWeek = (start.getDay() + i) % 7; // 0 = Sonntag, 1 = Montag, ..., 6 = Samstag
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        vacationDays++;
      }
    }

    return vacationDays;
  }*/

  const onComplete = async () => {
/*    if(localRulebreaks.value.length > 0)
    {
      await createDialog({
        title: "Regelverletzungen",
        text: `Es wurden Regelverletzungen festgestellt. Bitte diese korrigieren.`,
        buttons: [{title: "Ok", key: true}]
      });

      return;
    }
      
    createDialog({ title: "Daten abschicken",
      text: `Möchtest du deine Urlaubswünsche speichern und abschicken?`,
      buttons: [{ title: "Ja", key: true }, { title: "Nein", key: false }]})
        .then(async (answer) => {
          if(answer) {
            await onSave(true);
            await router.push(`/urlaubswuensche`)
          }
        })
    await onSave(true)
    if(employ.value)
    {
      await addDoc(collection(db, "mail"), {
        to: employ.value.email,
        message: {
          subject: "Deine Urlaubswünsche und Entlastungstage wurden eingereicht!",
          html:      "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              `  <span>Deine Urlaubswünsche und Entlastungstage für das Jahr <span style=\"color: #BE0081;\">${year}</span> wurden eingereicht!</span>\n` +
              "</div>\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              `  <span>Bis zur Deadline <span style=\"color: #BE0081;\">${vacationPeriod.value.deadline.toLocaleDateString("de-DE")}</span> kannst du die Wünsche noch anpassen!</span>\n` +
              "</div>\n" +
              "\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              "  <span>Es wird nun geprüft, in wie weit deine Wünsche <span style=\"color: #BE0081;\">berücksichtigt</span> werden können.</span>\n" +
              "</div>\n" +
              "\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              "  <span>Beachte, dass der Urlaub und Entlastungstage <span style=\"color: #BE0081;\">noch nicht bestätigt</span> sind!</span>\n" +
              "</div>\n" +
              "\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              "  <span>In der Zwischenzeit kannst du deine eingegebenen Wunschzeiten in der App oder deinen E-Mails <span style=\"color: #BE0081;\">einsehen.</span></span>\n" +
              "</div>\n" +
              "\n" +
              "<div style=\"font-family: Arial, sans-serif; line-height: 1.5; margin-bottom: 16px;\">\n" +
              "  <span>Sobald dein Urlaub und deine Entlastungstage bearbeitet wurden, bekommst du eine <span style=\"color: #BE0081;\">Benachrichtigung.</span></span>\n" +
              "</div>"
        },
      });
    }*/
    await router.push(`/urlaubswuensche`)
  };
  
  async function onSave(finished: boolean){
    let wasSaved = false;

    try{
      const existingId = vacationData.value?.id;
      const generatedId = `${vacationPeriod.value?.year ?? ""}${employ.value?.emplno ?? ""}`;
      const currentId = existingId || generatedId;

      if (!currentId) {
        throw new Error("VacationData-ID konnte nicht erzeugt werden.");
      }

      if (selectedPrio.value !== "ET") {
        const prio = Number(selectedPrio.value);
        vacationPhases.value[prio - 1].bemerkung = localBemerkung.value;
      }

      await InsertVacationData({
        id: currentId,
        vacationPeriodId: year,
        userId: userId,
        finished: finished,
        employNr: employ.value?.emplno,
        year: year,
        priorityOne: vacationPhases.value[0] as VacationPrio,
        priorityTwo: vacationPhases.value[1] as VacationPrio,
        priorityThree: vacationPhases.value[2] as VacationPrio,
        priorityFour: vacationPhases.value[3] as VacationPrio,
        reliefDays: reliefDays.value,
      });

      wasSaved = true;
      edited.value = false;
      notifySuccess("Daten erfolgreich gespeichert").then(() => {})
    }
    catch(e){
      console.error(e);
      notifyWarning("Daten konnten nicht gespeichert werden").then(() => {})
    }

    return wasSaved;
  }
  const { mapCurrent } = useScreens({
    xs: '0px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
  });
  const columns = mapCurrent({ lg: 2 }, 1);
  const expanded = mapCurrent({ sm: false, lg: true }, true);
  
  const getVariant = (value) => {
    return selectedPrio.value === value ? "flat" : "text";
  };
  
  const getBaseColor = (value) => {
    return selectedPrio.value === value ? "primary" : "secondary";
  }
  
  onMounted(async () => {
    try{
      isLoading.value = true;
      vacationPeriod.value = await GetVacationPeriod(year);
      shiftSettings.value = await GetShiftSetting(store.state.userData?.employData?.cycleid)

      try {
        ruleCfg.value = await GetRuleConfig();
      } catch (error) {
        console.error("Error loading info text:", error);
      }

      startDate.value = vacationPeriod.value.startDate;
      endDate.value = vacationPeriod.value.endDate;
      if(vacationPeriod.value.locked){
        overview.value = true;
        localEmployVacationData.value = await GetEmployVacationDataDate(employ.value?.emplno, convertUtcToGermanLocal(startDate.value), convertUtcToGermanLocal(endDate.value))
        console.log("EmployVacationData", localEmployVacationData.value)
        console.log("VacationBlocks", vacationBlocks.value)
      }

      console.log("Year", year, vacationPeriod)
      if (calendar.value && !overview.value) {
        await calendar.value.focusDate(startDate.value);
      }
      
      try {
        vacationData.value = await GetVacationData(year, employ.value?.emplno);
        vacationDataError.value = false;
      } catch (e) {
        console.error("[VAC-DATA] GetVacationData fehlgeschlagen:", e);
        vacationDataError.value = true;
      }
      console.log("VacationDATA", employ.value?.emplno)
      reliefDays.value = vacationData.value?.reliefDays ?? [];
      console.log("ReliefDays", reliefDays.value, vacationData.value);
      if(vacationData.value)
      {
        vacationPhases.value = [
          {
            index: 0,
            startDate: vacationData.value?.priorityOne?.startDate ?? null,
            endDate: vacationData.value?.priorityOne?.endDate ?? null,
            bemerkung: vacationData.value?.priorityOne?.bemerkung ?? "",
          },
          {
            index: 1,
            startDate: vacationData.value?.priorityTwo?.startDate ?? null,
            endDate: vacationData.value?.priorityTwo?.endDate ?? null,
            bemerkung: vacationData.value?.priorityTwo?.bemerkung ?? "",
          },
          {
            index: 2,
            startDate: vacationData.value?.priorityThree?.startDate ?? null,
            endDate: vacationData.value?.priorityThree?.endDate ?? null,
            bemerkung: vacationData.value?.priorityThree?.bemerkung ?? "",
          },
          {
            index: 3,
            startDate: vacationData.value?.priorityFour?.startDate ?? null,
            endDate: vacationData.value?.priorityFour?.endDate ?? null,
            bemerkung: vacationData.value?.priorityFour?.bemerkung ?? "",
          },
        ];
      }
      
      localBemerkung.value = vacationPhases.value[0].bemerkung;

      edited.value = false;
    }
    catch(e){
      console.error(e);
    }
    finally {
      isLoading.value = false;
    }

  });
  
  onBeforeRouteLeave((to, from, next) => {
    if(edited.value)
    {
      createDialog({ title: "Änderungen verwerfen?",
        text: `Möchtest du deine Änderungen wirklich verwerfen?`,
        buttons: [{ title: "Ja", key: true }, { title: "Nein", key: false }]})
          .then((answer) => {
            if(answer) {
              next();
            }
          })
    }
    else{
      next();
    }
  });

  interface DisabledDateRange {
    start: Date;
    end: Date;
  }

  const disabledDates = computed((): DisabledDateRange[] => {
    if (selectedPrio.value === 'ET' || overview.value) return [];
    
    const isValidDate = (d: unknown): d is Date => d instanceof Date && !isNaN(d.getTime());
    const currentPhaseIndex: number = Number(selectedPrio.value) - 1;
    const disabledRanges: DisabledDateRange[] = [];
    
    // Alle anderen Phasen durchgehen und deren Datumsbereiche sperren
    vacationPhases.value.forEach((phase: VacationPhase, index: number) => {
      if (index !== currentPhaseIndex && isValidDate(phase.startDate) && isValidDate(phase.endDate)) {
        disabledRanges.push({
          start: phase.startDate,
          end: phase.endDate
        });
      }
    });
    
    return disabledRanges;
  });

  // === TEMPORÄRE KALENDER-DIAGNOSE (nach dem Debuggen entfernen) ===
  // Loggt alle Daten, die an <v-calendar> gehen, und markiert ungültige (Invalid Date).
  watch(
    [attributes, disabledDates, startDate, endDate, () => selectedPrio.value, () => overview.value],
    () => {
      if (overview.value || selectedPrio.value === 'ET') return;
      const isBad = (d: unknown) => !(d instanceof Date) || isNaN((d as Date).getTime());
      const iso = (d: unknown) => (d instanceof Date && !isNaN(d.getTime()) ? d.toISOString() : String(d));

      const min = startDate.value;
      const max = endDate.value;
      const outOfRange = (d: Date) =>
        (min instanceof Date && !isNaN(min.getTime()) && d.getTime() < min.getTime()) ||
        (max instanceof Date && !isNaN(max.getTime()) && d.getTime() > max.getTime());

      // Vollständiger Snapshot als Tabelle/JSON
      console.log("[CAL-DIAG] === Kalender-Eingaben Snapshot ===");
      console.log("[CAL-DIAG] min-date:", iso(min), "max-date:", iso(max));
      console.log("[CAL-DIAG] disabledDates:", disabledDates.value.map(r => ({ start: iso(r.start), end: iso(r.end) })));
      console.log("[CAL-DIAG] attributes:", (attributes.value as any[]).map(a => ({
        key: a.key,
        dates: (a.dates ?? []).map((e: unknown) => Array.isArray(e) ? e.map(iso) : iso(e)),
      })));

      // min-date / max-date
      if (isBad(min)) console.warn("[CAL-DIAG] ⚠ min-date (startDate) ungültig:", min);
      if (isBad(max)) console.warn("[CAL-DIAG] ⚠ max-date (endDate) ungültig:", max);
      if (!isBad(min) && !isBad(max) && (min as Date).getTime() > (max as Date).getTime())
        console.warn("[CAL-DIAG] ⚠ min-date > max-date:", iso(min), ">", iso(max));

      // disabled-dates
      disabledDates.value.forEach((r, i) => {
        if (isBad(r.start) || isBad(r.end)) console.warn(`[CAL-DIAG] ⚠ disabledDates[${i}] ungültig:`, r);
        else if (r.start.getTime() > r.end.getTime()) console.warn(`[CAL-DIAG] ⚠ disabledDates[${i}] umgekehrt (start>end):`, iso(r.start), ">", iso(r.end));
      });

      // attributes: jedes einzelne Datum prüfen
      (attributes.value as any[]).forEach((attr) => {
        (attr.dates ?? []).forEach((entry: unknown, i: number) => {
          if (Array.isArray(entry)) {
            const [s, e] = entry as unknown[];
            if (isBad(s) || isBad(e)) console.warn(`[CAL-DIAG] ⚠ attribute "${attr.key}" range[${i}] ungültig:`, entry, attr);
            else {
              if ((s as Date).getTime() > (e as Date).getTime()) console.warn(`[CAL-DIAG] ⚠ attribute "${attr.key}" range[${i}] umgekehrt (start>end):`, iso(s), ">", iso(e));
              if (outOfRange(s as Date) || outOfRange(e as Date)) console.warn(`[CAL-DIAG] ⚠ attribute "${attr.key}" range[${i}] außerhalb min/max:`, iso(s), "-", iso(e));
            }
          } else if (isBad(entry)) {
            console.warn(`[CAL-DIAG] ⚠ attribute "${attr.key}" dates[${i}] ungültig:`, entry, attr);
          } else if (outOfRange(entry as Date)) {
            console.warn(`[CAL-DIAG] ⚠ attribute "${attr.key}" dates[${i}] außerhalb min/max:`, iso(entry), attr);
          }
        });
        if (!attr.dates || attr.dates.length === 0) {
          console.warn(`[CAL-DIAG] ⚠ attribute "${attr.key}" hat KEINE dates (leeres Array):`, attr);
        }
      });
    },
    { immediate: true, deep: true }
  );

  return {year, vacationPeriod, isLoading, localRulebreaks, bemerkungLabel, selectedPrio, calendar, columns, 
    onComplete, dayClick,toShortDateString,calculateVacationDays, startDate, wishedDays, sumUrlaub, endDate, 
    goBack, goToOverview, expanded,localBemerkung,vacationPhases, attributes, overview, selectedDateInput, 
    getVariant, getBaseColor, onSave, maxUrlaub, maxSonderUrlaub, deletePhase, entlastungsTage, reliefDays, 
    disabledDates, vacationShow, vacationBlocks, vacationDataError, vacationRulesText, vacationSummaryTopText, vacationSummaryBottomText};
}
}
</script>

<style scoped>
.prio-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  align-items: stretch;
  height: auto;
  min-height: unset;
}

.prio-btn {
  flex: 1 1 20%;
  min-width: 120px;
  width: 100%;
  box-sizing: border-box;
}

@media (max-width: 900px) {
  .prio-btn {
    flex: 1 1 32%; /* 3 Buttons pro Zeile */
    min-width: 140px;
    width: 100%;
  }
}
@media (max-width: 400px) {
  .prio-btn {
    flex: 1 1 48%; /* 2 Buttons pro Zeile */
    min-width: 120px;
    width: 100%;
  }
}
@media (max-width: 250px) {
  .prio-btn {
    flex: 1 1 100%; /* 1 Button pro Zeile */
    min-width: 100%;
    width: 100%;
  }
}
</style>